import type {
  AnyAction,
} from '@reduxjs/toolkit';
import {
  configureStore as configureStoreReduxToolkit,
} from '@reduxjs/toolkit';
import { listenToLocalStorage, loadLocalStorage } from 'common/utilities/browser/localStorage/localStorage';
import configurationMiddleware from 'core/redux/middlewares/configurationMiddleware';
import confirmationMiddleware from 'core/redux/middlewares/confirmationMiddleware';
import copyToClipboardMiddleware from 'core/redux/middlewares/copyToClipboardMiddleware';
import filterDuplicateLocationChangesMiddleware from 'core/redux/middlewares/filterDuplicateLocationChangesMiddleware';
import localStorageMiddleware from 'core/redux/middlewares/localStorageMiddleware';
import notificationGeneratorMiddleware from 'core/redux/middlewares/notificationGeneratorMiddleware';
import redirectMiddleware from 'core/redux/middlewares/redirectMiddleware';
import requestMiddleware from 'core/redux/middlewares/requestMiddleware';
import scrollMiddleware from 'core/redux/middlewares/scrollMiddleware';
import userMiddleware from 'core/redux/middlewares/userMiddleware';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import rootReducer from 'core/redux/reducers/rootReducer';
import entityHierarchyMiddleware from 'entityHierarchy/redux/middlewares/entityHierarchyMiddleware';
import type { History } from 'history';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { createReduxHistoryContext } from 'redux-first-history';
import thunk from 'redux-thunk';
import sequenceAnalyserMiddleware from 'sequenceAnalyser/redux/middlewares/sequenceAnalyserMiddleware';

import type { AppDispatch, AppStore } from './configureStore.types';

let store: AppStore;

/**
 * Create the redux app store which is composed of the reducers (initial state) and middlewares
 *
 * @param history
 * @param preloadedState only used for tests to load an initial state
 * @returns AppStore
 */
export default function configureStore(history: History, preloadedState?: Partial<ApplicationState>) {
  const {
    createReduxHistory,
    routerMiddleware,
    routerReducer,
  } = createReduxHistoryContext({ history });

  store = configureStoreReduxToolkit({
    middleware: [
      thunk,
      filterDuplicateLocationChangesMiddleware,
      notificationGeneratorMiddleware,
      redirectMiddleware,
      requestMiddleware,
      configurationMiddleware,
      userMiddleware,
      scrollMiddleware,
      confirmationMiddleware,
      sequenceAnalyserMiddleware,
      entityHierarchyMiddleware,
      localStorageMiddleware,
      copyToClipboardMiddleware,
      routerMiddleware,
    ],
    reducer: (state: ApplicationState | undefined, action: AnyAction) => rootReducer(state, action, routerReducer),
    preloadedState,
  });

  loadLocalStorage(store);
  listenToLocalStorage(store);

  const appHistory = createReduxHistory(store);

  return { appStore: store, appHistory };
}

export const getStore = () => store;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<ApplicationState> = useSelector;
